// Take data records
// For each record generate a report
// Return a zip file of all reports

import pptxgen from "pptxgenjs";
import logo from '../images/mindset_logo_white.jpg';
import habit1 from '../images/habit1.png';
import habit2 from '../images/habit2.png';
import habit3 from '../images/habit3.png';
import habit4 from '../images/habit4.png';
import habit5 from '../images/habit5.png';
import habit6 from '../images/habit6.png';
import slide3Table from '../images/slide3Table.png';

import bulletPoints from '../data/bulletPoints.json';
import paragraphs from '../data/paragraphs.json'
import JSZip from "jszip";
import { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import { ReactComponent as ZipFile } from "../images/zip_file.svg";
import { Button } from "bootstrap";


// Capitalises first letter and lowercases rest
function properNounify(string) {
    let words = string.split(" ");
    let alteredString = [];
    for (let i in words) {
        let altered = words[i].toLowerCase();
        altered = altered[0].toUpperCase() + altered.slice(1);
        alteredString.push(altered)
    }
    return alteredString.join(" ");
}

function bytesToSize(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return 'n/a'
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)), 10)
    if (i === 0) return `${bytes} ${sizes[i]})`
    return `${(bytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`
}

// Calls functions to generate report for each record
function handleRecords(records) {
    return Promise.all(
        records.map(async (data, index) => {
            let stats = calculateStats(data);
            let buffer = await generateReport(stats.name, stats.performanceScore, stats.habitPercents, stats.habitScoreProportions, stats.dominantParagraph, stats.secondaryParagraph, stats.inferiorParagraph, stats.strengthBulletPoints, stats.developmentBulletPoints, stats.fiveWords, stats.sortableHabitScores).write('arraybuffer')
            let nameString = stats.name.split(" ").join("");
            let dateString = stats.date.replaceAll('/','-');
            return {
                buffer, filename: `${nameString}_${dateString}.pptx`
            };
        })
    )
};

// Calls report generator function and creates a zip file containing all the report pptx
function createZipFile(presentationData) {
    const zip = new JSZip();
    
    presentationData.forEach(({ buffer, filename }) => {
        zip.file(filename, new Uint8Array(buffer));
    });
    
    return zip.generateAsync({ type: 'blob' });
}

// Calls zip function and makes it available to download
function handleDownload(records, changeStateLoading, changeStateLoaded, changeStateFileSize, changeStateFileNumber) {
    changeStateLoading(true);
    handleRecords(records)
        .then((data) => createZipFile(data))
        .then((zipBlob) => {
            changeStateLoading(false);
            changeStateLoaded(true);
            changeStateFileNumber(records.length);
            changeStateFileSize(bytesToSize(zipBlob.size))
            const downloadLink = document.createElement('a');
            downloadLink.href = URL.createObjectURL(zipBlob);
            downloadLink.download = 'Reports.zip';
            downloadLink.textContent = 'Download Reports';
            downloadLink.style.display = 'block';
            downloadLink.className = "Primary-button-large"
            document.getElementById('downloadContainer').appendChild(downloadLink);
        })
        .catch((error) => {
            console.error('Error creating zip file:', error);
        });
};
  

// Gets points for each answer depending on the rank given, rank 1 = 6, rank 2 = 5, etc.
function getHabitScore(rank) {
    return Math.abs(parseInt(rank) - 7);
}

function calculateStats(record) {
    // Get date of response
    let date = record[2].split(" ")[0];
    // Get respondant's full name
    let name = record[10] !== null ? `${record[9]} ${record[10]}` : record[9];

    // Get respondant's performance score
    let performanceScore = record[16];
    
    // Get scores for each habit
    let habits = ["live in the gap", "be a connector", "find a way", "dare to fail", "show up", "live to serve"];
    let habitScores = {"live in the gap": 0, "be a connector": 0, "find a way": 0, "dare to fail": 0, "show up": 0, "live to serve": 0};

    let currentIndex = 17; // The index where the rank questions start
    let currentHabit = 0;
    let strengthBulletPoints = [];
    let developmentBulletPoints = [];
    let questionNum = 0;
    while (currentIndex <= 58) { // The index where the rank questions end
        if (currentHabit > 5) { // Loop through the habits, if all habits have been cycled through, we are on a new question
            currentHabit = 0;
            questionNum += 1;
        }
        // For each question, add points to each habit depending on rank given
        // For every question except Q11 (index 2): for rank 1, add corresponding bulletpoints to strengths, for rank 6, add bulletpoints to developments
        
        if (questionNum !== 2) {
            habitScores[habits[currentHabit]] = habitScores[habits[currentHabit]] + getHabitScore(record[currentIndex]);
            // console.log(`Index: ${currentIndex}, Rank: ${record[currentIndex]}, Current Habit: ${habits[currentHabit]}, Current Question: ${questionNum}`);
            if (record[currentIndex] === 1) {
                strengthBulletPoints = strengthBulletPoints.concat(bulletPoints[`q${questionNum}`][habits[currentHabit]]);
            } else if (record[currentIndex] === 6) {
                developmentBulletPoints = developmentBulletPoints.concat(bulletPoints[`q${questionNum}`][habits[currentHabit]]);
            }
        } else {
            habitScores[habits[currentHabit]] = habitScores[habits[currentHabit]] + parseInt(record[currentIndex]);
        }
        currentHabit += 1;
        currentIndex += 1;
    }
    

    let habitScoreTotal = 0
    for (let habit in habitScores) {
        habitScoreTotal += habitScores[habit];
    }

    let habitPercents = {};
    let habitScoreProportions = {};
    for (let habit in habitScores) {
        habitPercents[habit] = Math.round((habitScores[habit] / 42) * 100);
        habitScoreProportions[habit] = Math.round((habitScores[habit] / habitScoreTotal) * 100);
    }
    
    // Get dominant, secondary, and inferior paragraphs
    let sortableHabitScores = [];
    for (let habit in habitScores) {
        sortableHabitScores.push([habit, habitScores[habit]])
    }
    sortableHabitScores.sort(function(a, b) {
        return a[1] - b[1];
    })
    let secondaryParagraph = paragraphs['secondary'][sortableHabitScores[4][0]];
    let inferiorParagraph = paragraphs['inferior'][sortableHabitScores[0][0]];
    let firstName = name.split(" ")[0];
    let dominantParagraph = paragraphs['dominant'][sortableHabitScores[5][0]].replace("{name}", properNounify(firstName));
    
    // Get respondant's five key words
    let fiveWords = [record[59], record[60], record[61], record[62],record[63]];

    // console.log(name); // String
    // console.log(performanceScore); // String
    // console.log(habitPercents); // Object
    // console.log(habitScoreProportions) // Object
    // console.log(dominantParagraph); // String
    // console.log(secondaryParagraph); // String
    // console.log(inferiorParagraph); // String
    // console.log(strengthBulletPoints); // Array[String]
    // console.log(developmentBulletPoints); // Array[String]
    // console.log(fiveWords); // Array[String]
    return {date, name, performanceScore, habitPercents, habitScoreProportions, dominantParagraph, secondaryParagraph, inferiorParagraph, strengthBulletPoints, developmentBulletPoints, fiveWords, sortableHabitScores};
}

function generateReport(name, performanceScore, habitPercents, habitScoreProportions, dominantParagraph, secondaryParagraph, inferiorParagraph, strengthBulletPoints, developmentBulletPoints, fiveWords, sortableHabitScores) {
    // Create a new Presentation
    let pptx = new pptxgen();
    pptx.defineLayout({name: "A4", width: 7.5, height: 10.833});
    pptx.layout = "A4";
    pptx.theme = { headFontFace: "Rockwell", bodyFontFace: "Rockwell"};
    pptx.defineSlideMaster({
        title: "MASTER_SLIDE",
        background: {color: "FFFFFF" },
        objects: [
            { image: {x: "37.9%", y: "1%", w: "2", h: "1.727", path: logo} }
        ]
    })
    let black = "000000";
    let orange = "F15927";
    let green = "226979";
    let gray = "cfd6e3";
    let titleStyle = { x: "0%", y: 2.15, w: "100%", color: black, isTextBox: true , align: "center", bold: true, fontSize: 20};
    let habitImages = {"live in the gap": habit1, "be a connector": habit2, "find a way": habit3, "dare to fail": habit4, "show up": habit5, "live to serve": habit6};
    
    // Slide 1 - No dynamic positioning - Complete
    let slide1 = pptx.addSlide({ masterName: "MASTER_SLIDE" });
    // Title
    slide1.addText("Micro Assessment Report", titleStyle);
    // Habit Charts
    function habitChartGenerator(slide, habit, x, y) {
        let habitTitleStyle = { w: 2.42, autoFit: true, color: green, isTextBox: true , align: "center", bold: true, fontSize: 20};
        let habitChartStyle = {
            align: "center",
            w: 1.8,
            h: 1.8,
            holeSize: 75,
            showLabel: false,
            showValue: false,
            showPercent: false,
            chartColors: [orange, gray],
            dataBorder: {pt: 2, color: "F1F1F1"}
        };
        let habitPercentStyle = { w: 0.7, autoFit: true, color: black, isTextBox: true , align: "left", bold: true, fontSize: 12};
        let dists = {titleX: 0, titleY: 0.09, chartImageX: 0.55, chartImageY: 0.55, chartImageW: 1.3, chartImageH: 1.3, chartX: 0.31, chartY: 0.32, percentX: 1.76, percentY: 1.79}
        let data = [
            {
                name: properNounify(habit),
                labels: ["Yes", "No"],
                values: [habitPercents[habit], 100 - habitPercents[habit]],
            }
        ];
        slide.addText(properNounify(habit), {...habitTitleStyle, ...{x: x+dists["titleX"], y: y+dists["titleY"]}})
        slide.addImage({ path: habitImages[habit], x: x+dists["chartImageX"], y: y+dists["chartImageY"], w: dists["chartImageW"], h: dists["chartImageH"] });
        slide.addChart(pptx.charts.DOUGHNUT, data, {...habitChartStyle, ...{x: x+dists["chartX"], y: y+dists["chartY"]}});
        slide.addText(`${habitPercents[habit]}%`, {...habitPercentStyle, ...{x: x+dists["percentX"], y: y+dists["percentY"]}})
    }

    habitChartGenerator(slide1, "live in the gap", 0.246, 3.071);
    habitChartGenerator(slide1, "be a connector", 2.608, 3.071);
    habitChartGenerator(slide1, "find a way", 4.97, 3.071);
    habitChartGenerator(slide1, "dare to fail", 0.246, 5.512);
    habitChartGenerator(slide1, "show up", 2.608, 5.512);
    habitChartGenerator(slide1, "live to serve", 4.97, 5.512);
    // Title
    slide1.addText("Your MicroLeadership Wheel:", { w: "100%", x:0, y: 8., color: black, isTextBox: true , align: "center", bold: true, fontSize: 18});
    // Performance Score
    slide1.addText("Your Performance Score:", { w: 2, x:5.35, y: 9, color: black, isTextBox: true , align: "right", bold: true, fontSize: 18})
    slide1.addText(`${performanceScore}%`, {w: 2, x:5.35, y: 9.7, color: green, isTextBox: true , align: "right", bold: true, fontSize: 32})
    // Proportion Wheel
    let pieData = [
        {
            name: "Pie Chart",
            labels: ["Live in the Gap", "Be a Connector", "Find a Way", "Dare to Fail", "Show Up", "Live to Serve"],
            values: [habitPercents["live in the gap"], habitPercents["be a connector"], habitPercents["find a way"], habitPercents["dare to fail"], habitPercents["show up"], habitPercents["live to serve"]]
        }
    ]
    let pieChartStyle = {
        align: "center",
        x: 1.85,
        y: 8.2,

		showLabel: false,
		showValue: false,
		showPercent: true,
        showLegend: true,
        chartColors: ["F15927","C85C37","CFD6E3","DAB7A5","4B6669","226979"],
        dataLabelColor: black,
        dataLabelFontSize: 14, 
        dataLabelFontFace: "Rockwell",
        dataLabelFontBold: true,
        dataLabelPosition: "t",
        showLeaderLines: true,
        dataBorder: {pt: 2, color: "F1F1F1"}
	};
    slide1.addChart(pptx.charts.PIE, pieData, {...pieChartStyle, ...{x: 1.85, y: 8.2, legendPos: "b", h: 2.5, w:4}})
    // Five Words
    slide1.addText("Your Five Words:", { w: 3, x:0.3, y: 8.65, color: black, isTextBox: true , align: "left", bold: true, fontSize: 18})
    slide1.addText(`${fiveWords[0]}\n${fiveWords[1]}\n${fiveWords[2]}\n${fiveWords[3]}\n${fiveWords[4]}`, {w: 3, x:0.3, y: 8.8, color: green, bullet: true, isTextBox: true , align: "left", bold: true, fontSize: 18, valign: "top"})
    
    // Slide 2 - No dynamic positioning - Complete
    let slide2 = pptx.addSlide({ masterName: "MASTER_SLIDE" });
    //Title
    slide2.addText("How to interpret your Micro Assessment", titleStyle);
    // Dominant Habit
    let dominantHabit = sortableHabitScores[5][0]
    habitChartGenerator(slide2, dominantHabit, 0.246, 3.071);

    let habitExplanation = "Each habit is shown as indicated here – this gives a rating against this habit as a percentage score out of the maximum possible. The higher the score the greater focus you are giving this habit currently as a leader.";
    let wheelExplanation = "The Micro Leadership Wheel takes the individual scores and distributes them across the wheel. This gives an indication of the relative level of focus across the 6 habits.\nThis clearly shows your dominant Micro Habit and your Inferior Habit.";
    slide2.addText(habitExplanation, {x: 3.073, y: 4.05, w: 3.866, color: black, isTextBox: true , align: "left", bold: false, fontSize: 16});
    // Proportion Wheel
    slide2.addChart(pptx.charts.PIE, pieData, {...pieChartStyle, ...{x: 1.7, y: 5.7, legendPos: "r", h: 2.5, w: 4}})
    slide2.addText(wheelExplanation, {x: 0.75, y: 9.2, w: 6, color: black, isTextBox: true , align: "center", bold: false, fontSize: 16})

    // Slide 3 - No dynamic positioning - Complete
    let slide3 = pptx.addSlide({ masterName: "MASTER_SLIDE" });
    // Title
    slide3.addText("MicroLeadership Habits: Overview", titleStyle);
    // Habit Table
    slide3.addImage({ path: slide3Table, x: "5%", y: 2.45, w: "90%", h: "76%"});

    // Slide 4 - Dynamic positoning
    let slide4 = pptx.addSlide({ masterName: "MASTER_SLIDE" });
    slide4.addText("Overall Summary", titleStyle);
    // Paragraphs
    let paraArray = [
        { text: `${dominantParagraph}\n`, options: {breakLine: true} },
        { text: `${secondaryParagraph}\n`, options: {breakLine: true} },
        { text: `${inferiorParagraph}\n`, options: {} },
    ];
    slide4.addText(paraArray, {x: "5%", y: 4.35, w: "90%", fontSize: 11});
    // Strengths and Weaknesses
    let strengthsString = strengthBulletPoints.join("\n");
    let developmentString = developmentBulletPoints.join("\n");
    // slide1.addText("Your Five Words:", { w: 3, x:0.3, y: 8.65, color: black, isTextBox: true , align: "left", bold: true, fontSize: 18})
    let tableRows = [
        [
            {text: "Leadership Strengths", options: {color: green, fontSize: 15, align: "center", bold: true}},
            {text: "Leadership Development Areas", options: {color: green, fontSize: 15, align: "center", bold: true}}
        ],
        [
            {text: strengthsString, options: {bullet: { indent: 10 }}}, 
            {text: developmentString, options: {bullet: { indent: 10 }}}
        ]
    ]
    slide4.addTable(tableRows, {w: "95%", x: "2.5%", y: "56%", color: black, bullet: {type: "bullet"}, fontSize: 10, rowH: [0.3, 4.3], border: {type: "solid", pt: 2, color: green}});
    // Slide 5 - Complete
    let slide5 = pptx.addSlide({ masterName: "MASTER_SLIDE" });
    // Response box
    slide5.addShape(pptx.ShapeType.rect, { line: { color: green, width: 4 }, h: "78%", w: "90%", align: "center", x: "5%", y: 2.2 });
    slide5.addText("What does this mean to me?", { x: "5.8%", y: 2.4, w: "100%", color: green, isTextBox: true, align: "left", bold: false, fontSize: 18});

    console.log("Generated Report")
    // Save the Presentation
    return pptx;
}
function ReportGenerator(props) {
    const [loading, setLoading] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [fileSize, setFileSize] = useState(null);
    const [fileNumber, setFileNumber] = useState(null);
    const onChangeLoading = (newState) => {
        setLoading(newState);
    }
    const onChangeLoaded = (newState) => {
        setLoaded(newState);
    }
    const onChangeFileSize = (newState) => {
        setFileSize(newState);
    }
    const onChangeFileNumber = (newState) => {
        setFileNumber(newState);
    }
    useEffect(() => {
        handleDownload(props.records, onChangeLoading, onChangeLoaded, onChangeFileSize, onChangeFileNumber)
    }, []);
    return (
        <div className="csv-upload">
            <ClipLoader
                color="#F15927"
                loading={loading}
                size={150}
                aria-label="Loading Spinner"
                data-testid="loader"
            />
            {loaded && <ZipFile height={200} width={200} />}
            {fileSize && fileNumber && <span className="info">{fileNumber} File{(fileNumber > 1) ? "s": ""} - {fileSize}</span>}
            <div id="downloadContainer"></div>
            {loaded && <button className="Secondary-button-medium" onClick={() => {window.location.reload()}}>Home</button>}
        </div>
    )
}

export default ReportGenerator;